import { MouseEvent, useEffect, useRef } from 'react';
import Image from 'next/image';
import { SearchItemWithImageViewModel } from '@/types/api';
import { getSearchResultItemURL } from '@/features/navigation/utils/url-helpers';
import { AppNextLink } from '@/features/app-next-link';
import { Button } from '@/features/button';
import { IconMarketing } from '@/features/icons';
import { Binoban } from '@/utils/binoban';
import { cn } from '@/utils/cn';
import { handleImageError } from '@/utils/image-error';
import MobileSearchLink from './mobile-search-link';
import { SearchResultItemProps } from './types';
import { useSearchImpression } from './utils/use-search-impressions';
interface Props {
  query: string;
  title: string;
  items: SearchItemWithImageViewModel[] | null;
  isModalOpen?: boolean;
  handleClickItem: (e: MouseEvent, href: string) => void;
}
const SearchResultProducts = ({
  title,
  items,
  query,
  isModalOpen,
  handleClickItem
}: Props) => {
  const {
    resetImpressions
  } = useSearchImpression();
  const queryId = items?.at(0)?.queryId;
  useEffect(() => {
    if (isModalOpen && queryId) {
      resetImpressions();
    }
  }, [isModalOpen, queryId, resetImpressions]);
  if (!items?.length) return null;
  const hasAdInList = items.some(item => item.ad != null);
  const showAllLinkHref = `/search?query=${query}`;
  return <div className='flex w-full flex-col gap-2 text-text-black lg:px-1' data-sentry-component="SearchResultProducts" data-sentry-source-file="search-result-products.tsx">
            <span className='text-sm font-medium'>{title}</span>
            <div className='flex flex-col items-start gap-2 overflow-auto lg:gap-1 lg:overflow-hidden' data-test='search-result--products'>
                {items.map(item => <SearchResultItem key={item.id} item={item} hasAdInList={hasAdInList} clickHandler={(e, href) => handleClickItem(e, href)} />)}
                <Button graphicStyle='text' size='medium' className='flex flex-row gap-1 pr-0' data-sentry-element="Button" data-sentry-source-file="search-result-products.tsx">
                    <AppNextLink href={showAllLinkHref} className='flex w-full items-center gap-2' prefetch={false} onClick={e => handleClickItem(e, showAllLinkHref)} data-sentry-element="AppNextLink" data-sentry-source-file="search-result-products.tsx">
                        مشاهده همه محصولات
                    </AppNextLink>
                </Button>
            </div>
        </div>;
};
const SearchResultItem = ({
  item,
  hasAdInList,
  clickHandler
}: SearchResultItemProps) => {
  const itemRef = useRef<HTMLDivElement>(null);
  const {
    seenIds,
    markAsSeen
  } = useSearchImpression();
  const itemName = `${item.nameFa} ${item.brand?.nameFa}`;
  const itemHref = getSearchResultItemURL(item);
  useEffect(() => {
    const node = itemRef.current;
    if (!node) return;
    const queryId = item.queryId ?? '';
    if (!item.ad || seenIds.has(`${queryId}-${item.ad.bidId}`)) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && entry.intersectionRatio === 1) {
        if (item.ad) {
          Binoban.adsImpressionedEvent(item.ad.index ?? null, item.ad.bidId ?? null);
          markAsSeen(`${queryId}-${item.ad.bidId}`);
        }
        observer.unobserve(entry.target);
      }
    }, {
      threshold: 1.0
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [item.ad, seenIds, markAsSeen, item.queryId]);
  const handleClick = (e: MouseEvent) => {
    if (item.ad) {
      Binoban.adsClickedEvent(item.ad.index ?? null, item.ad.bidId ?? null);
    }
    clickHandler(e, itemHref);
  };
  if (!item?.imageUrl) return null;
  return <div className='w-full' ref={itemRef} data-sentry-component="SearchResultItem" data-sentry-source-file="search-result-products.tsx">
            <MobileSearchLink className={cn('flex h-full w-full items-center justify-between gap-2 rounded py-1 text-center', item?.imageUrl ? 'bg-surface-solid-0' : 'bg-surface-solid-50')} href={itemHref} item={item} onClick={e => handleClick(e)} data-sentry-element="MobileSearchLink" data-sentry-source-file="search-result-products.tsx">
                <Image unoptimized src={item.imageUrl} alt={itemName || ''} width={48} height={48} className='h-12 w-12 shrink-0' onError={handleImageError} data-sentry-element="Image" data-sentry-source-file="search-result-products.tsx" />
                <div className='flex grow items-center gap-4'>
                    <span className={cn('grow text-center text-xs leading-5 lg:text-sm lg:leading-6', item?.imageUrl ? 'line-clamp-2 text-right' : 'text-text-darkGray', hasAdInList && !item.ad && 'pl-4')}>
                        {itemName}
                    </span>
                    {item?.ad && <IconMarketing className='min-h-4 min-w-4 text-primary-main lg:h-5 lg:w-5' />}
                </div>
            </MobileSearchLink>
        </div>;
};
export default SearchResultProducts;